// components/monitoring/WanPriorityControl.js
//
// Priority chip for the Monitoring page WAN table (desktop + mobile), and
// the click-to-select dialog behind it. No drag-and-drop.
//
// SAFETY RULES (do not relax):
//   - The table rows are REPORT data (/api/monitoring/*-data). They are
//     used ONLY to label the chip and to identify which WAN was clicked.
//     They are NEVER used to build a write payload.
//   - The payload is built from a FRESH live fetch (the same one
//     LiveWanPanel uses), and that fetch is repeated right before sending.
//   - The clicked row must match exactly one live, primary WAN by BOTH
//     name and id, otherwise nothing is sent.
//   - Every send goes through wanPriority.checkPriorityChange (DC block,
//     secondary block, payload validation, keep-one-connected safety net).
//   - Whether the control is enabled at all (role, today/daily, device
//     type) is decided by the parent via `enabled`.

import React, { useState, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import { changeWanPriority } from "../../utils/routerLiveData";
import {
  loadLiveWans,
  checkPriorityChange,
  recordPendingPriorities,
  payloadWanId,
  wanName,
  toDisplayPriority,
  reportPriorityLabel,
  shortPriorityLabel,
  priorityLabel,
  isOfflineStatus,
} from "../../utils/wanPriority";

const OPTIONS = [0, 1, 2, 3, 999];
const OPTION_HINT = {
  0: "Carries traffic whenever it is up",
  1: "Used when every P1 is down",
  2: "Only when P1 and P2 are down",
  3: "Last in the failover ladder",
  999: "Removed from failover entirely",
};

// Find the live WAN for a report row. Must match by name AND (when the row
// has an id) by id, and must be a primary WAN (not a secondary-router WAN,
// which is shown with a lock in LiveWanPanel and never sent).
export function findLiveTarget(liveWans, row, isFullyManaged) {
  const rowName = row?.name;
  const rowId = row?.raw?.id;
  if (!rowName) return { error: "This interface has no name - priority cannot be changed here." };

  const byName = liveWans.filter((w) => wanName(w) === rowName);
  if (byName.length === 0) return { error: `"${rowName}" was not found in the router's live WAN list.` };
  if (byName.length > 1) return { error: `More than one live WAN is named "${rowName}" - change it from the Site Dashboard instead.` };

  const live = byName[0];
  const liveId = payloadWanId(live, isFullyManaged);
  if (liveId === null || liveId === undefined) {
    return { error: `"${rowName}" belongs to a secondary router - its priority cannot be changed.` };
  }
  if (rowId !== undefined && rowId !== null && String(rowId) !== String(liveId)) {
    return { error: `"${rowName}" does not match the live WAN (id ${rowId} vs ${liveId}) - nothing was changed.` };
  }
  return { target: live };
}

export default function WanPriorityControl({ row, serialNumber, managementType, deviceName, scope, enabled, onChanged, chipClass = "chip-other" }) {
  const [open, setOpen] = useState(false);
  const label = reportPriorityLabel(row?.priority);

  if (!enabled) {
    return <span className={`t247-chip ${chipClass}`}>{label}</span>;
  }

  return (
    <>
      <button
        type="button"
        className={`t247-chip ${chipClass} t247-prio-chip-btn`}
        title="Change priority"
        aria-label={`Change priority for ${row?.name || "this WAN"}`}
        onClick={(e) => {
          e.stopPropagation(); // the row itself opens the detail modal
          setOpen(true);
        }}
        onKeyDown={(e) => e.stopPropagation()}
      >
        {label} ▾
      </button>
      {open && (
        <PriorityDialog
          row={row}
          serialNumber={serialNumber}
          managementType={managementType}
          deviceName={deviceName}
          scope={scope}
          onClose={() => setOpen(false)}
          onChanged={onChanged}
        />
      )}
    </>
  );
}

function PriorityDialog({ row, serialNumber, managementType, deviceName, scope, onClose, onChanged }) {
  const isFullyManaged = managementType === "fully_managed";
  const [phase, setPhase] = useState("loading"); // loading | ready | saving | done
  const [error, setError] = useState(null);
  const [target, setTarget] = useState(null);
  const [selected, setSelected] = useState(null);

  const load = useCallback(async () => {
    setPhase("loading");
    setError(null);
    try {
      const liveWans = await loadLiveWans(managementType, serialNumber, deviceName, scope);
      const found = findLiveTarget(liveWans, row, isFullyManaged);
      if (found.error) {
        setError(found.error);
        setTarget(null);
      } else {
        setTarget(found.target);
        setSelected(toDisplayPriority(found.target.priority, isFullyManaged));
      }
    } catch (e) {
      setError(e.message || "Could not load the router's live WAN data.");
      setTarget(null);
    } finally {
      setPhase("ready");
    }
  }, [managementType, serialNumber, deviceName, scope, row, isFullyManaged]);

  useEffect(() => {
    load();
  }, [load]);

  const currentDP = target ? toDisplayPriority(target.priority, isFullyManaged) : null;

  const save = async () => {
    if (!target || selected === null || phase === "saving") return;
    setPhase("saving");
    setError(null);
    try {
      // Re-fetch right before sending, so the payload reflects the router's
      // state NOW, not when the dialog was opened.
      const liveWans = await loadLiveWans(managementType, serialNumber, deviceName, scope);
      const found = findLiveTarget(liveWans, row, isFullyManaged);
      if (found.error) throw new Error(found.error);
      if (toDisplayPriority(found.target.priority, isFullyManaged) !== currentDP) {
        // Someone/something changed it since the dialog opened - make the
        // user look again rather than act on what they saw.
        setTarget(found.target);
        setSelected(toDisplayPriority(found.target.priority, isFullyManaged));
        setError("This WAN's priority changed since you opened this dialog. Please review and try again. Nothing was sent.");
        setPhase("ready");
        return;
      }

      const check = checkPriorityChange(liveWans, found.target, selected, managementType);
      if (check.status === "noop") {
        onClose();
        return;
      }
      if (check.status !== "ok") {
        if (check.validationError) console.error("[WanPriorityControl] Priority payload refused:", check.validationError, check.payload);
        setError(check.message);
        setPhase("ready");
        return;
      }

      await changeWanPriority(serialNumber, check.payload);
      recordPendingPriorities(serialNumber, check.payload);
      setPhase("done");
      onChanged && onChanged();
      setTimeout(onClose, 900);
    } catch (e) {
      setError(e.message || "Failed to update WAN priority.");
      setPhase("ready");
    }
  };

  const busy = phase === "loading" || phase === "saving";
  const status = (target?.status || target?.status_text || "").toLowerCase();

  const content = (
    <div className="t247-modal-overlay t247" onClick={busy ? undefined : onClose}>
      <div className="t247-modal" style={{ maxWidth: 440 }} onClick={(e) => e.stopPropagation()}>
        <div className="t247-modal-header">
          <span className="t247-modal-title">SET WAN PRIORITY</span>
          <button className="t247-modal-close" onClick={onClose} disabled={phase === "saving"} aria-label="Close">×</button>
        </div>
        <div className="t247-modal-info">
          <span>{row?.name}</span>
          {target && (
            <span>
              LIVE: {shortPriorityLabel(currentDP)} · {status ? (isOfflineStatus(status) ? "DISCONNECTED" : "CONNECTED") : "—"}
            </span>
          )}
        </div>

        <div className="t247-modal-body">
          {phase === "loading" && <div className="t247-prio-note">Loading the router's live WAN data…</div>}

          {phase === "done" && <div className="t247-prio-note ok">Priority updated.</div>}

          {error && <div className="t247-prio-error">{error}</div>}

          {target && phase !== "loading" && phase !== "done" && (
            <div className="t247-prio-options" role="radiogroup" aria-label="Priority">
              {OPTIONS.map((p) => {
                const isCurrent = p === currentDP;
                const isSelected = p === selected;
                return (
                  <button
                    type="button"
                    key={p}
                    role="radio"
                    aria-checked={isSelected}
                    className={"t247-prio-option" + (isSelected ? " selected" : "")}
                    disabled={phase === "saving"}
                    onClick={() => setSelected(p)}
                  >
                    <span className="t247-prio-option-title">
                      {p === 999 ? "Disable this WAN" : priorityLabel(p)}
                      {isCurrent && <span className="t247-prio-current">CURRENT</span>}
                    </span>
                    <span className="t247-prio-option-hint">{OPTION_HINT[p]}</span>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        <div className="t247-modal-footer">
          <button className="t247-btn t247-btn-ghost" onClick={onClose} disabled={phase === "saving"}>CANCEL</button>
          {target ? (
            <button
              className="t247-btn t247-btn-primary"
              onClick={save}
              disabled={busy || phase === "done" || selected === null || selected === currentDP}
            >
              {phase === "saving" ? "SAVING…" : "SAVE"}
            </button>
          ) : (
            phase !== "loading" && <button className="t247-btn t247-btn-primary" onClick={load}>RETRY</button>
          )}
        </div>
      </div>
      <style dangerouslySetInnerHTML={{ __html: PRIORITY_CSS }} />
    </div>
  );

  return createPortal(content, document.getElementById("modal-root") || document.body);
}

const PRIORITY_CSS = `
  .t247-prio-chip-btn { cursor: pointer; border: 1px solid rgba(136, 244, 235, 0.45); font: inherit; }
  .t247-prio-chip-btn:hover { border-color: var(--turquoise, #88F4EB); }
  .t247-prio-options { display: flex; flex-direction: column; gap: 6px; }
  .t247-prio-option {
    display: flex; flex-direction: column; align-items: flex-start; gap: 3px; width: 100%;
    padding: 11px 14px; text-align: left; cursor: pointer;
    background: transparent; color: inherit; border: 1px solid rgba(255, 255, 255, 0.14);
  }
  .t247-prio-option:hover { background: rgba(255, 255, 255, 0.04); }
  .t247-prio-option.selected { border-color: var(--turquoise, #88F4EB); background: rgba(136, 244, 235, 0.08); }
  .t247-prio-option:disabled { cursor: default; opacity: 0.6; }
  .t247-prio-option-title { font: 700 13px var(--font-sans, sans-serif); display: flex; align-items: center; gap: 8px; }
  .t247-prio-option-hint { font: 400 11px var(--font-mono, monospace); color: var(--text-muted, rgba(255,255,255,.55)); }
  .t247-prio-current { font: 400 9px var(--font-mono, monospace); letter-spacing: 0.1em; padding: 1px 6px; background: rgba(255,255,255,0.13); }
  .t247-prio-note { font: 400 12px var(--font-mono, monospace); color: var(--text-dim, rgba(255,255,255,.7)); padding: 6px 11px 12px; }
  .t247-prio-note.ok { color: var(--turquoise, #88F4EB); }
  .t247-prio-error {
    white-space: pre-line; margin-bottom: 12px; padding: 10px 12px; font: 400 12px var(--font-sans, sans-serif);
    border-left: 2px solid var(--neon, #CAFB6D); background: rgba(255, 255, 255, 0.05);
  }
`;
